/**
 * Model add — Angular `model-add.component` parity (Phase 2).
 */

import { AlertCircle, Loader2 } from 'lucide-react'
import { PageHeader } from '@/features/invitee/components/PageHeader'
import { SectionCard } from '@/components/ui/SectionCard'
import {
  primaryEntityActionButtonClass,
  secondaryEntityActionButtonClass,
} from '@/components/ui/entityActionButtonClasses'
import { cn } from '@/lib/utils/helpers'
import formStyles from '@/features/application/styles/editForm.module.css'
import { ModelCalculationParamsPanel } from '../components/ModelCalculationParamsPanel'
import { ModelFileField } from '../components/ModelFileField'
import { useModelAdd } from '../hooks/useModelAdd'
import styles from '../styles/modelAdd.module.css'

export function ModelAddPage() {
  const {
    definitions,
    loading,
    submitting,
    validateForm,
    form,
    processorFile,
    isDirty,
    updateField,
    updateCalculationParams,
    setProcessorFile,
    setPklFile,
    cancel,
    submit,
  } = useModelAdd()

  const showNameError = validateForm && !form.model_name.trim()
  const showProcessorError = validateForm && !processorFile

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    void submit()
  }

  return (
    <div
      className="min-h-screen bg-[#f8fafc] pb-12 font-sans text-slate-900"
      data-testid="model-add-page"
    >
      <PageHeader title="Add Model" testIdRoot="model-add-page-header" />

      <div className="mx-auto w-full space-y-6 px-3 py-4 sm:px-6 sm:py-8 lg:px-8">
        {loading ? (
          <div
            className="flex min-h-[320px] items-center justify-center rounded-lg border border-slate-200 bg-white py-20 shadow-sm"
            data-testid="model-add-loading"
          >
            <Loader2 className="h-8 w-8 animate-spin text-[rgb(21_151_255)]" aria-label="Loading" />
          </div>
        ) : (
          <form
            className="space-y-6"
            onSubmit={handleSubmit}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && event.target instanceof HTMLInputElement) {
                event.preventDefault()
              }
            }}
            data-testid="model-add-form"
          >
            <div className="flex flex-wrap items-center justify-end gap-2">
              <button
                type="button"
                className={secondaryEntityActionButtonClass}
                onClick={cancel}
                disabled={submitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className={primaryEntityActionButtonClass}
                disabled={submitting || !isDirty}
              >
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                    Submitting...
                  </>
                ) : (
                  'Submit'
                )}
              </button>
            </div>

            <SectionCard number={1} title="Model Information" testId="model-add-section-info" className={styles.modelFormPremium}>
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                <div className="space-y-4">
                  <div className={cn(formStyles.formGroup, styles.formGroupFull)}>
                    <label htmlFor="model_name" className={styles.fieldLabel}>
                      Model Name<span className={formStyles.required}>*</span>
                    </label>
                    <input
                      id="model_name"
                      type="text"
                      className={cn(formStyles.input, showNameError && formStyles.inputError)}
                      placeholder="Enter model name"
                      value={form.model_name}
                      onChange={(event) => updateField('model_name', event.target.value)}
                    />
                    {showNameError ? (
                      <div className={styles.fieldError}>
                        <AlertCircle size={16} aria-hidden />
                        Model Name is required
                      </div>
                    ) : null}
                  </div>

                  <div className={cn(formStyles.formGroup, styles.formGroupFull)}>
                    <label htmlFor="model_description" className={styles.fieldLabel}>
                      Description
                    </label>
                    <textarea
                      id="model_description"
                      rows={4}
                      className={cn(formStyles.textarea, styles.textarea)}
                      placeholder="Enter model description"
                      value={form.description}
                      onChange={(event) => updateField('description', event.target.value)}
                    />
                  </div>

                  <div className={cn(formStyles.formGroup, styles.formGroupFull)}>
                    <span className={styles.fieldLabel}>Status</span>
                    <div className="flex items-center gap-2">
                      <label className={styles.switch}>
                        <input
                          type="checkbox"
                          checked={form.is_active === 1}
                          onChange={(event) => updateField('is_active', event.target.checked ? 1 : 0)}
                        />
                        <span className={styles.slider} />
                      </label>
                      <span className="text-sm font-medium text-[#334155]">
                        {form.is_active === 1 ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <ModelFileField
                    id="model_processor_file"
                    label="Model Processor File"
                    labelNote="(.py, Maximum file size: 50MB)"
                    accept=".py"
                    required
                    error={showProcessorError ? 'Model Processor File is required' : null}
                    onChange={setProcessorFile}
                  />

                  <ModelFileField
                    id="model_pkl_file"
                    label="Model PKL File"
                    labelNote="(Optional, .pkl, Maximum file size: 50MB)"
                    accept=".pkl"
                    onChange={setPklFile}
                  />
                </div>
              </div>
            </SectionCard>

            <SectionCard title="Variables used for model calculation" testId="model-add-section-variables">
              <ModelCalculationParamsPanel
                definitions={definitions}
                value={form.calculationParams}
                onChange={updateCalculationParams}
              />
            </SectionCard>
          </form>
        )}
      </div>
    </div>
  )
}
